'use client'

// React Imports

// MUI Imports
import { useParams } from 'next/navigation'

import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'

// Component Imports
import IconifyIcon from '@/@core/components/icon'

// Hook Imports
import { useWeatherTranslations } from '@/hooks/useTranslations'

// Next.js Imports

// Utils Imports
import {
  formatTemperature,
  getTemperatureColor,
  filterFutureHourly,
  formatTimeString,
  formatPercentage,
  formatWindSpeed,
  formatPrecipitation,
  getUVIndexColor,
  formatUVIndex,
  formatVisibility,
  formatPressure,
  getDayOfWeek,
  getDayPeriodInfo
} from '@/utils/weatherUtils'

// Types
import type { WeatherData } from '@/types/weather/weatherTypes'

interface CompactViewProps {
  data: WeatherData
  isExpanded: boolean
  renderWeatherIcon: (conditionCode: number, size?: number, isDay?: boolean, timeString?: string) => React.ReactNode
}

const CompactView = ({ data, isExpanded, renderWeatherIcon }: CompactViewProps) => {
  const theme = useTheme()
  const { t: _t } = useWeatherTranslations()
  const params = useParams()
  const currentLang = (params?.lang as string) || 'pt'

  // Dados principais
  const todayForecast = data.forecast[0]

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Stack
        direction='row'
        alignItems='center'
        spacing={2.5}
        sx={{
          minHeight: 65,
          pr: 5,
          width: '100%',
          justifyContent: { xs: 'space-around', sm: 'flex-start' } // Espalha elementos em telas pequenas
        }}
      >
        {/* Bloco 1: Localização */}
        <Stack alignItems='center' spacing={0.5}>
          <Typography
            variant='h5'
            fontWeight={700}
            sx={{
              lineHeight: 1.2,
              textAlign: 'center',
              fontSize: '1.25rem'
            }}
          >
            {data.location.name}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              lineHeight: 1,
              fontSize: '0.85rem',
              textAlign: 'center'
            }}
          >
            {data.location.region}
          </Typography>
        </Stack>

        {/* Bloco 2: Ícone e temperaturas min/max */}
        <Stack alignItems='center' spacing={1}>
          {renderWeatherIcon(data.current.condition.code, 40, data.current.isDay, data.current.lastUpdated)}
          <Stack direction='row' alignItems='center' spacing={1}>
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <IconifyIcon
                icon='ri:arrow-up-line'
                fontSize='0.8rem'
                color={theme.palette.error.main}
              />
              <Typography variant='body2' fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                {formatTemperature(todayForecast.dayData.temperature.max, data.unitSystem)}
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <IconifyIcon
                icon='ri:arrow-down-line'
                fontSize='0.8rem'
                color={theme.palette.info.main}
              />
              <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.8rem' }}>
                {formatTemperature(todayForecast.dayData.temperature.min, data.unitSystem)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {/* Bloco 3: Temperatura atual e condição */}
        <Stack alignItems='center' spacing={0.5}>
          <Typography
            variant='h3'
            fontWeight={700}
            sx={{
              lineHeight: 1,
              color: getTemperatureColor(data.current.temperature, theme.palette.mode === 'dark'),
              textAlign: 'center'
            }}
          >
            {formatTemperature(data.current.temperature, data.unitSystem)}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              lineHeight: 1.2,
              fontSize: '0.8rem',
              textAlign: 'center',
              maxWidth: 120
            }}
          >
            {data.current.condition.text}
          </Typography>
        </Stack>

        {/* Bloco 4: Próximas horas - Primeiro a sumir em telas pequenas */}
        <Stack
          spacing={0.8}
          sx={{
            minWidth: 180,
            display: { xs: 'none', lg: 'flex' } // Oculta em xs, sm, md - mostra apenas em lg+
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.6rem', fontWeight: 600, textAlign: 'center' }}>
            {_t('forecast.hourly')}
          </Typography>
          <Stack direction='row' spacing={1.5} sx={{ justifyContent: 'center' }}>
            {filterFutureHourly(todayForecast.hourlyData).slice(0, 5).map((hour, _index) => (
              <Stack key={hour.time} alignItems='center' spacing={0.3} sx={{ minWidth: 50 }}>
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.55rem', fontWeight: 500 }}>
                  {formatTimeString(hour.time)}
                </Typography>
                {renderWeatherIcon(hour.condition.code, 25, getDayPeriodInfo(hour.time).isDay, hour.time)}
                <Typography variant='caption' fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                  {formatTemperature(hour.temperature, data.unitSystem)}
                </Typography>
                {hour.chanceOfRain > 0 && (
                  <Stack direction='row' alignItems='center' spacing={0.2}>
                    <IconifyIcon icon='ri:drop-line' fontSize='0.5rem' color={theme.palette.info.main} />
                    <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.5rem' }}>
                      {formatPercentage(hour.chanceOfRain)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Bloco 5: Detalhes climáticos - Segundo a sumir */}
        <Stack
          direction='row'
          spacing={2}
          sx={{ display: { xs: 'none', md: 'flex' } }} // Oculta em xs e sm - mostra em md+
        >
          <Stack spacing={0.3}>
            <Stack direction='row' alignItems='center' spacing={0.6}>
              <IconifyIcon icon='ri:drop-line' fontSize='0.75rem' color={theme.palette.info.main} />
              <Typography variant='body2' sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                {_t('current.humidity')}: {formatPercentage(data.current.humidity)}
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.6}>
              <IconifyIcon icon='ri:thermometer-line' fontSize='0.75rem' color={theme.palette.warning.main} />
              <Typography variant='body2' sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                {_t('current.feelsLike')}: {formatTemperature(data.current.feelsLike, data.unitSystem)}
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.6}>
              <IconifyIcon icon='ri:windy-line' fontSize='0.75rem' color={theme.palette.success.main} />
              <Typography variant='body2' sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                {_t('current.wind')}: {formatWindSpeed(data.current.windSpeed, data.unitSystem)}
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.6}>
              <IconifyIcon icon='ri:rainy-line' fontSize='0.75rem' color={theme.palette.primary.main} />
              <Typography variant='body2' sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                {_t('forecast.precipitation')}: {formatPrecipitation(data.current.precipitation, data.unitSystem)}
              </Typography>
            </Stack>
          </Stack>

          <Stack spacing={0.3}>
            <Stack direction='row' alignItems='center' spacing={0.6}>
              <IconifyIcon
                icon='ri:sun-line'
                fontSize='0.75rem'
                color={getUVIndexColor(data.current.uv)}
              />
              <Typography variant='body2' sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                {_t('current.uvIndex')}: {formatUVIndex(data.current.uv)}
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.6}>
              <IconifyIcon icon='ri:eye-line' fontSize='0.75rem' color={theme.palette.grey[600]} />
              <Typography variant='body2' sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                {_t('current.visibility')}: {formatVisibility(data.current.visibility, data.unitSystem)}
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.6}>
              <IconifyIcon icon='ri:cloud-line' fontSize='0.75rem' color={theme.palette.grey[500]} />
              <Typography variant='body2' sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                {_t('current.cloudiness')}: {formatPercentage(data.current.cloud)}
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.6}>
              <IconifyIcon icon='ri:dashboard-line' fontSize='0.75rem' color={theme.palette.grey[600]} />
              <Typography variant='body2' sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                {_t('current.pressure')}: {formatPressure(data.current.pressure, data.unitSystem)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {/* Divider - Oculto apenas em xs */}
        <Divider orientation="vertical" flexItem sx={{ mx: 3, opacity: 0.8, display: { xs: 'none', sm: 'flex' } }} />

        {/* Bloco 6: Próximos dias - Último a sumir (apenas em xs) */}
        <Stack
          direction='row'
          spacing={1.5}
          sx={{
            justifyContent: 'center',
            display: { xs: 'none', sm: 'flex' } // Oculta apenas em xs - mostra em sm+
          }}
        >
          {data.forecast.slice(1, 3).map((day) => {
            const dayOfWeek = getDayOfWeek(day.date, currentLang === 'en' ? 'en' : 'pt')

            return (
              <Stack key={day.date} alignItems='center' spacing={0.4} sx={{ minWidth: 75 }}>
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                  {dayOfWeek}
                </Typography>
                {renderWeatherIcon(day.dayData.condition.code, 30, true)}
                <Stack direction='row' alignItems='center' spacing={0.4}>
                  <Stack direction='row' alignItems='center' spacing={0.2}>
                    <IconifyIcon
                      icon='ri:arrow-up-line'
                      fontSize='0.65rem'
                      color={theme.palette.error.main}
                    />
                    <Typography variant='caption' fontWeight={600} sx={{ fontSize: '0.7rem' }}>
                      {formatTemperature(day.dayData.temperature.max, data.unitSystem)}
                    </Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center' spacing={0.2}>
                    <IconifyIcon
                      icon='ri:arrow-down-line'
                      fontSize='0.65rem'
                      color={theme.palette.info.main}
                    />
                    <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.65rem' }}>
                      {formatTemperature(day.dayData.temperature.min, data.unitSystem)}
                    </Typography>
                  </Stack>
                </Stack>
                <Typography variant='caption' color='text.secondary' sx={{
                  fontSize: '0.6rem',
                  textAlign: 'center',
                  maxWidth: 70,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {day.dayData.condition.text}
                </Typography>
              </Stack>
            )
          })}
        </Stack>
      </Stack>

        {/* Botão de expansão - fixo no canto direito - oculto quando só mostra básico */}
        <Box
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            display: { xs: 'none', sm: 'flex' }, // Oculto em xs (quando só mostra básico)
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24
          }}
        >
          <IconifyIcon
            icon={isExpanded ? 'ri:arrow-up-s-line' : 'ri:arrow-down-s-line'}
            fontSize='1.2rem'
            color={theme.palette.text.secondary}
            style={{ cursor: 'pointer' }}
          />
        </Box>
    </Box>
  )
}

export default CompactView
